import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapPin, X, Plus, Loader2 } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";
import { SearchableSelect, SearchableMultiSelect } from "@/components/SearchableSelect";
import PhoneInput from "@/components/PhoneInput";

const EMPTY = {
  name: "", category_ids: [], description: "",
  provider_private_phone: "", provider_private_whatsapp: "", email: "", website: "",
  address: "", province: "", district: "", neighborhood: "",
  service_provinces: [], service_districts: [],
  latitude: 38.4823, longitude: 28.1391,
  logo_url: "", cover_url: "", gallery: [], services: [],
  is_24_7: false,
  hours: Object.fromEntries([...Array(7).keys()].map((i) => [String(i), { open: "08:00", close: "20:00", closed: false }])),
};
const DAYS = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

export default function BusinessForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [servicesText, setServicesText] = useState("");
  const [serviceDistrictOptions, setServiceDistrictOptions] = useState({});
  const [geoLoading, setGeoLoading] = useState(false); // eslint-disable-line no-unused-vars

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/locations/provinces").then(({ data }) => setProvinces(data));
    if (id) {
      api.get(`/my/businesses`).then(({ data }) => {
        const mine = data.find((b) => b.id === id);
        if (mine) {
          setF({ ...EMPTY, ...mine });
          setServicesText((mine.services || []).join(", "));
          // preload service districts for each selected service province
          (mine.service_provinces || []).forEach((p) => {
            api.get("/locations/districts", { params: { province: p } })
              .then(({ data }) => setServiceDistrictOptions((prev) => ({ ...prev, [p]: data })));
          });
          // preload districts for current province
          if (mine.province) api.get("/locations/districts", { params: { province: mine.province } }).then(({ data }) => setDistricts(data));
        }
      });
    }
  }, [id, user, loading, nav]);

  useEffect(() => {
    if (f.province) api.get("/locations/districts", { params: { province: f.province } }).then(({ data }) => setDistricts(data));
    else setDistricts([]);
  }, [f.province]);

  const upd = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const toggleCat = (cid) => {
    setF((s) => ({
      ...s,
      category_ids: s.category_ids.includes(cid) ? s.category_ids.filter((x) => x !== cid) : [...s.category_ids, cid],
    }));
  };

  const setServiceProvinces = async (list) => {
    setF((s) => ({ ...s, service_provinces: list }));
    for (const p of list) {
      if (!serviceDistrictOptions[p]) {
        const { data } = await api.get("/locations/districts", { params: { province: p } });
        setServiceDistrictOptions((prev) => ({ ...prev, [p]: data }));
      }
    }
    // drop districts from removed provinces
    setF((s) => ({
      ...s,
      service_districts: s.service_districts.filter((d) =>
        list.some((p) => (serviceDistrictOptions[p] || []).includes(d))
      ),
    }));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS desteklenmiyor");
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(async (p) => {
      const lat = p.coords.latitude, lng = p.coords.longitude;
      setF((s) => ({ ...s, latitude: lat, longitude: lng }));
      try {
        const { data } = await api.get("/geo/reverse", { params: { lat, lng } });
        if (data?.ok) {
          setF((s) => {
            const nxt = { ...s, latitude: lat, longitude: lng };
            if (data.province) nxt.province = data.province;
            if (data.district) nxt.district = data.district;
            if (data.neighborhood) nxt.neighborhood = data.neighborhood;
            if (data.display_name && !s.address) nxt.address = data.display_name;
            return nxt;
          });
          toast.success("Konum ve adres alındı");
        } else {
          toast.info("Konum alındı, adres çözülemedi");
        }
      } catch {
        toast.info("Konum alındı");
      } finally {
        setGeoLoading(false);
      }
    }, () => { setGeoLoading(false); toast.error("Konum alınamadı"); }, { timeout: 10000 });
  };

  const uploadFile = async (file, key) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    if (key === "gallery") setF((s) => ({ ...s, gallery: [...(s.gallery || []), data.url] }));
    else setF((s) => ({ ...s, [key]: data.url }));
    toast.success("Yüklendi");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.category_ids.length === 0) return toast.error("En az bir hizmet kategorisi seçin");
    if (!f.province || !f.district) return toast.error("İl ve ilçe seçin");
    const payload = { ...f, services: servicesText.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (id) {
        await api.put(`/businesses/${id}`, payload);
        toast.success("İşletme güncellendi");
      } else {
        await api.post("/businesses", payload);
        toast.success("Başvurunuz alındı. Admin onayı bekleniyor.");
      }
      nav("/panel");
    } catch { toast.error("Bir hata oluştu"); }
  };

  const setHour = (day, field, value) => {
    setF((s) => ({ ...s, hours: { ...s.hours, [day]: { ...s.hours[day], [field]: value } } }));
  };

  // Aggregate all service districts across chosen provinces (unique)
  const availableServiceDistricts = Array.from(
    new Set(f.service_provinces.flatMap((p) => serviceDistrictOptions[p] || []))
  ).sort((a, b) => a.localeCompare(b, "tr"));

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <h1 className="text-2xl font-bold tracking-tight mb-4">{id ? "İşletme Düzenle" : "Yeni Hizmet Sağlayıcı Başvurusu"}</h1>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-2xl border border-border p-4 space-y-3">
          <div><Label>İşletme Adı *</Label><Input data-testid="bf-name" value={f.name} onChange={upd("name")} required className="mt-1" /></div>
          <div>
            <Label>Hizmet Kategorileri * (birden fazla seçebilirsiniz)</Label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto p-2 border rounded-xl">
              {cats.map((c) => (
                <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm ${f.category_ids.includes(c.id) ? "bg-primary/20" : "hover:bg-secondary"}`}>
                  <Checkbox data-testid={`cat-${c.slug}`} checked={f.category_ids.includes(c.id)} onCheckedChange={() => toggleCat(c.id)} />
                  <span>{c.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div><Label>Açıklama</Label><Textarea data-testid="bf-desc" value={f.description} onChange={upd("description")} rows={3} className="mt-1" /></div>
          <div><Label>Hizmet Detayları (virgülle)</Label><Input value={servicesText} onChange={(e) => setServicesText(e.target.value)} placeholder="Örn: 40 tonluk lowbed" className="mt-1" /></div>
        </div>

        <div className="rounded-2xl border border-border p-4 space-y-3">
          <h3 className="font-semibold">Özel İletişim <span className="text-xs text-muted-foreground font-normal">(sadece siz ve admin görürsünüz)</span></h3>
          <div className="rounded-xl bg-primary/10 border border-primary/40 p-3 text-xs">
            Bu numara müşteriye asla gösterilmez. Yol Hattı ileride 0850 sanal numara atayacaktır.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Özel Telefon (GSM)</Label><PhoneInput testid="bf-phone" value={f.provider_private_phone} onChange={upd("provider_private_phone")} /></div>
            <div><Label>Özel WhatsApp</Label><PhoneInput testid="bf-wa" value={f.provider_private_whatsapp} onChange={upd("provider_private_whatsapp")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>E-posta</Label><Input value={f.email} onChange={upd("email")} className="mt-1" /></div>
            <div><Label>Web Sitesi</Label><Input value={f.website} onChange={upd("website")} className="mt-1" /></div>
          </div>
        </div>

        <div className="rounded-2xl border border-border p-4 space-y-3">
          <h3 className="font-semibold">Merkez Konum</h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>İl *</Label>
              <SearchableSelect testid="bf-province" value={f.province} onChange={(v) => setF((s) => ({ ...s, province: v, district: "" }))} options={provinces} placeholder="İl seçin" />
            </div>
            <div>
              <Label>İlçe *</Label>
              <SearchableSelect testid="bf-district" value={f.district} onChange={upd("district")} options={districts} placeholder="İlçe seçin" disabled={!f.province} />
            </div>
          </div>
          <div><Label>Mahalle</Label><Input data-testid="bf-neighborhood" value={f.neighborhood} onChange={upd("neighborhood")} className="mt-1" placeholder="Mahalle adı" /></div>
          <div><Label>Açık Adres</Label><Input value={f.address} onChange={upd("address")} className="mt-1" /></div>
          <div>
            <Label>Haritadan Konum Seç</Label>
            <LocationPicker
              testid="bf-map"
              value={f.latitude ? { lat: f.latitude, lng: f.longitude } : null}
              currentContext={{ province: f.province, district: f.district }}
              onChange={({ lat, lng, province, district, neighbourhood, road, address }) => {
                setF((s) => {
                  const nxt = { ...s, latitude: lat, longitude: lng };
                  // Only auto-fill when user has not manually typed
                  if (province && !s.province) nxt.province = province;
                  if (district && !s.district) nxt.district = district;
                  if (neighbourhood && !s.neighborhood) nxt.neighborhood = neighbourhood;
                  if (address && !s.address) nxt.address = road || address;
                  return nxt;
                });
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Enlem</Label><Input type="number" step="0.0001" value={f.latitude} onChange={(e) => setF((s) => ({ ...s, latitude: parseFloat(e.target.value) }))} className="mt-1" /></div>
            <div><Label>Boylam</Label><Input type="number" step="0.0001" value={f.longitude} onChange={(e) => setF((s) => ({ ...s, longitude: parseFloat(e.target.value) }))} className="mt-1" /></div>
          </div>
        </div>

        <div className="rounded-2xl border border-border p-4 space-y-3">
          <h3 className="font-semibold">Hizmet Bölgeleri</h3>
          <div>
            <Label>Hizmet Verilen İller</Label>
            <SearchableMultiSelect testid="bf-sp" values={f.service_provinces} onChange={setServiceProvinces} options={provinces} placeholder="İl ekle" />
          </div>
          {f.service_provinces.length > 0 && (
            <div>
              <Label>Hizmet Verilen İlçeler</Label>
              <SearchableMultiSelect testid="bf-sd" values={f.service_districts} onChange={(v) => setF((s) => ({ ...s, service_districts: v }))} options={availableServiceDistricts} placeholder="İlçe ekle" />
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border p-4 space-y-3">
          <h3 className="font-semibold">Görseller</h3>
          <div><Label>Kapak Fotoğrafı</Label><input data-testid="bf-cover" type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "cover_url")} className="mt-1 block" />{f.cover_url && <span className="text-xs text-muted-foreground">✓ yüklendi</span>}</div>
          <div><Label>Logo</Label><input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "logo_url")} className="mt-1 block" /></div>
          <div>
            <Label>Galeri</Label>
            <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "gallery")} className="mt-1 block" />
            <div className="flex flex-wrap gap-2 mt-2">
              {(f.gallery || []).map((g, i) => (
                <div key={g} className="relative"><img src={g.startsWith("http") ? g : `${process.env.REACT_APP_BACKEND_URL}${g}`} className="w-16 h-16 object-cover rounded-lg" alt="" />
                  <button type="button" onClick={() => setF((s) => ({ ...s, gallery: s.gallery.filter((_, x) => x !== i) }))} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">Çalışma Saatleri</h3>
            <label className="flex items-center gap-2 text-sm">
              <Switch data-testid="bf-24-7" checked={f.is_24_7} onCheckedChange={upd("is_24_7")} />
              <span className="font-medium">7/24 hizmet</span>
            </label>
          </div>
          {!f.is_24_7 && DAYS.map((d, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="w-24">{d}</span>
              {f.hours[String(i)]?.closed ? (
                <span className="text-muted-foreground flex-1">Kapalı</span>
              ) : (
                <>
                  <Input type="time" value={f.hours[String(i)]?.open || "08:00"} onChange={(e) => setHour(String(i), "open", e.target.value)} className="h-9 w-28" />
                  <Input type="time" value={f.hours[String(i)]?.close || "20:00"} onChange={(e) => setHour(String(i), "close", e.target.value)} className="h-9 w-28" />
                </>
              )}
              <Button type="button" size="sm" variant="outline" onClick={() => setHour(String(i), "closed", !f.hours[String(i)]?.closed)}>
                {f.hours[String(i)]?.closed ? "Aç" : "Kapat"}
              </Button>
            </div>
          ))}
        </div>

        <Button data-testid="bf-submit" type="submit" className="w-full h-12 rounded-full press bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
          <Plus className="w-4 h-4 mr-1" /> {id ? "Güncelle" : "Başvuruyu Gönder"}
        </Button>
      </form>
    </div>
  );
}
